import { getWidgetStores, InfoCard } from '@/entities/widget';
import { TrendingUpIcon } from 'lucide-react';
import { DetailList } from '../DetailList/DetailList';
import { DetailSection } from '../DetailSection/DetailSection';
import { DetailStack } from '../DetailStack/DetailStack';
import { SectionTitle } from '../SectionTitle/SectionTitle';
import type { MeasurementAndActivationSectionProps } from './MeasurementAndActivationSection.types';
import { useTranslation } from 'react-i18next';

export function MeasurementAndActivationSection(props: MeasurementAndActivationSectionProps) {
  const { t } = useTranslation('contentTest');

  return (
    <div data-testid="social-brief-measurement-activation-section">
      <SectionTitle
        title={t('socialBriefWidget.measurementAndActivationSection.measurementActivation')}
        icon={<TrendingUpIcon className="size-5" />}
        tooltip={t('socialBriefWidget.measurementAndActivationSection.outlinesHowSuccessWillBe')}
      />
      <DetailSection>
        <PlatformRecommendation {...props} />
        <AssumptionsFlags {...props} />
        <AssetTypeNeeds {...props} />
      </DetailSection>
    </div>
  );
}

function PlatformRecommendation({ item }: MeasurementAndActivationSectionProps) {
  const { t } = useTranslation('contentTest');

  // ChatMarkdownRenderer via the entities/widget binding seam.
  const { ChatMarkdownRenderer } = getWidgetStores().chatStore;
  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.measurementAndActivationSection.platformRecommendation')}
      </span>

      <InfoCard style="w-full">
        <DetailStack>
          <span className="text-body-lg font-medium text-muted-foreground">
            {t('socialBriefWidget.measurementAndActivationSection.primary')}
          </span>

          <div className="text-body-lg text-foreground">
            <ChatMarkdownRenderer
              content={item.measurementActivation.platformRecommendation.primary}
              disableAnimation
              inline
            />
          </div>
        </DetailStack>
      </InfoCard>

      <InfoCard style="w-full">
        <DetailStack>
          <span className="text-body-lg font-medium text-muted-foreground">
            {t('socialBriefWidget.measurementAndActivationSection.secondary')}
          </span>

          <div className="text-body-lg text-foreground">
            <ChatMarkdownRenderer
              content={item.measurementActivation.platformRecommendation.secondary}
              disableAnimation
              inline
            />
          </div>
        </DetailStack>
      </InfoCard>

      <InfoCard style="w-full">
        <DetailStack>
          <span className="text-body-lg font-medium text-muted-foreground">
            {t('socialBriefWidget.measurementAndActivationSection.rationale')}
          </span>

          <DetailList>
            {item.measurementActivation.platformRecommendation.rationales.map((rationale) => (
              <li key={rationale} className="text-body-lg text-foreground">
                <ChatMarkdownRenderer content={rationale} disableAnimation inline />
              </li>
            ))}
          </DetailList>
        </DetailStack>
      </InfoCard>
    </DetailStack>
  );
}

function AssumptionsFlags({ item }: MeasurementAndActivationSectionProps) {
  const { t } = useTranslation('contentTest');

  // ChatMarkdownRenderer via the entities/widget binding seam.
  const { ChatMarkdownRenderer } = getWidgetStores().chatStore;
  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.measurementAndActivationSection.assumptionsFlags')}
      </span>

      <InfoCard style="w-full">
        <DetailList>
          {item.measurementActivation.assumptionsOrFlags.map((flag) => (
            <li key={flag} className="text-body-lg text-foreground">
              <ChatMarkdownRenderer content={flag} disableAnimation inline />
            </li>
          ))}
        </DetailList>
      </InfoCard>
    </DetailStack>
  );
}

function AssetTypeNeeds({ item }: MeasurementAndActivationSectionProps) {
  const { t } = useTranslation('contentTest');

  // ChatMarkdownRenderer via the entities/widget binding seam.
  const { ChatMarkdownRenderer } = getWidgetStores().chatStore;
  return (
    <DetailStack>
      <span className="text-body-lg font-medium text-foreground">
        {t('socialBriefWidget.measurementAndActivationSection.assetTypeNeedS')}
      </span>

      <InfoCard style="w-full">
        <DetailList>
          {item.measurementActivation.assetTypeNeeds.map((assetTypeNeed) => (
            <li key={assetTypeNeed} className="text-body-lg text-foreground">
              <ChatMarkdownRenderer content={assetTypeNeed} disableAnimation inline />
            </li>
          ))}
        </DetailList>
      </InfoCard>
    </DetailStack>
  );
}
